import { type ReactNode } from "react";

import {
  type ProjectFeedAttachment,
  type ProjectFeedEvent,
  type ProjectSuggestionResolution,
} from "../../../services/askPunttProjectFeed.types";
import { FeedCoachingCard } from "../components/FeedCoachingCard";
import { FeedImportCard } from "../components/FeedImportCard";
import { FeedMemoryCard } from "../components/FeedMemoryCard";
import { FeedPost } from "../components/FeedPost";
import { FeedSuggestionCard } from "../components/FeedSuggestionCard";
import { FeedVersionDiffCard } from "../components/FeedVersionDiffCard";

export interface ProjectFeedCardHandlers {
  onResolveSuggestion?: (
    eventId: string,
    action: ProjectSuggestionResolution,
    payload?: Record<string, unknown>,
  ) => void;
  resolvingEventId?: string | null;
  onOpenAttachment?: (attachment: ProjectFeedAttachment) => void;
  onOpenReview?: (reviewRunId: string) => void;
  onAskPrivately?: () => void;
}

/**
 * Render a single project feed event as its card, for interleaving into the
 * shared chat timeline (the project feed is the live chat + these cards).
 *
 * Returns `null` for `agent_user_turn` / `agent_assistant_turn`: those are the
 * shared chat's real messages, which the embedded chat renders with full
 * fidelity (streaming, agent steps, canvas), so they must NOT be duplicated as
 * static feed cards. Every other event type renders as its specialized card,
 * falling back to the generic FeedPost (project_created, owner_kickoff,
 * version_added, review_started, member_invited, pm_comment_synced, …).
 */
export function renderProjectFeedCard(
  event: ProjectFeedEvent,
  handlers: ProjectFeedCardHandlers = {},
): ReactNode {
  switch (event.type) {
    // Excluded — rendered as real chat messages by the embedded AskPunttChat.
    case "agent_user_turn":
    case "agent_assistant_turn":
      return null;
    case "integration_import":
      return <FeedImportCard event={event} />;
    case "memory_saved":
    case "instruction_committed":
      return <FeedMemoryCard event={event} />;
    case "suggestion_issued":
      return (
        <FeedSuggestionCard
          event={event}
          isResolving={handlers.resolvingEventId === event._id}
          onResolve={(action, payload) =>
            handlers.onResolveSuggestion?.(event._id, action, payload)
          }
        />
      );
    case "coaching":
      return (
        <FeedCoachingCard
          event={event}
          onAskPrivately={handlers.onAskPrivately}
        />
      );
    case "version_diff":
      return (
        <FeedVersionDiffCard
          event={event}
          onOpenReview={handlers.onOpenReview}
        />
      );
    case "member_invited": {
      const userIds = (event.payload?.userIds as string[] | undefined) ?? [];
      const body =
        event.body?.trim() ||
        (userIds.length === 1
          ? "Invited a teammate to the project."
          : userIds.length > 1
            ? `Invited ${userIds.length} teammates to the project.`
            : "Invited new members to the project.");
      return (
        <FeedPost
          event={{ ...event, body }}
          onOpenAttachment={handlers.onOpenAttachment}
        />
      );
    }
    default:
      return (
        <FeedPost event={event} onOpenAttachment={handlers.onOpenAttachment} />
      );
  }
}

/** True for feed events that are the shared chat's real messages (excluded). */
export function isAgentTurnEvent(event: ProjectFeedEvent): boolean {
  return (
    event.type === "agent_user_turn" || event.type === "agent_assistant_turn"
  );
}
